import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { putRequest } from "../../services/apiMethods";
import toast from "react-hot-toast";
import { isOpeningHoursEqual } from "../../utils/HelperFunctions";
import { ContactInfoValidation } from "../../utils/Validations";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";



const ContactInfo = ({ data: existData, refresh }) => {

  const initialValues = {
    email: "",
    phoneNumber: "",
    countryCode: "",
    openingHours: Array(7).fill({ from: "", to: "", is24HourAvailable: false }),
  };
  const [phone, setPhone] = useState("");

  const { mutate, isLoading: apiState } = useMutation({
    mutationFn: (data) => {
      let modifyData

      if (isOpeningHoursEqual(existData?.openingHours)) {
        if (isOpeningHoursEqual(data?.openingHours)) {
          modifyData = {
            email: data?.email || "",
            phoneNumber: data?.phoneNumber || "",
            countryCode: data?.countryCode || "",
            
            openingHours: data?.openingHours || Array(7).fill({ from: "", to: "", is24HourAvailable: false }),
          };
        }
        else {
          modifyData = {
            email: data?.email || "",
            percentage: data?.percentage + 10,
            phoneNumber: data?.phoneNumber || "",
            countryCode: data?.countryCode || "",
            openingHours: data?.openingHours || Array(7).fill({ from: "", to: "", is24HourAvailable: false }),
          };
        }
      } else {
        modifyData = {
          email: data?.email || "",
          phoneNumber: data?.phoneNumber || "",
          countryCode: data?.countryCode || "",
          openingHours: data?.openingHours || Array(7).fill({ from: "", to: "", is24HourAvailable: false }),
        };
      }
      return putRequest(`/profile/edit`, modifyData);
    },
    onSuccess: (data) => {
      toast.success(`${data?.message}`, {
        iconTheme: {
          primary: '#337FE0',
          secondary: '#FFFAEE',
        },
      })
      refresh()
    },
    onError: (error) => {
      console.error("Error during API call:", error);
    },
  });



  const handleSubmit = (values) => {
    // Validate openingHours for missing 'from' or 'to'
    const invalidOpeningHours = values.openingHours.some(day => {
      if ((day.from && !day.to) || (!day.from && day.to)) {
        return true; // From is filled, but To is not, or To is filled, but From is not
      }

      // If both 'from' and 'to' are filled, check if 'from' is less than 'to'
      if (day.from && day.to) {
        const fromTime = new Date(`1970-01-01T${day.from}:00`);
        const toTime = new Date(`1970-01-01T${day.to}:00`);

        if (fromTime >= toTime) {
          return true; // From time should not be later than To time
        }
      }

      return false;
    });
    if (invalidOpeningHours) {
      toast.error("Please make sure both 'From' and 'To' are filled correctly and 'From' time is earlier than 'To' time for each day.");
      return;
    }

    if (isOpeningHoursEqual(values?.openingHours)) {
      toast.error("Opening Times is required");
      return;
    }

    mutate(values);
  };

  const fillAllOpeningHours = (setFieldValue, openingHours) => {
    // Check if at least one day has 'from' and 'to' or 'is24HourAvailable' filled
    const isValidOpeningHours = openingHours.some(day =>
      (day.from && day.to) || day.is24HourAvailable
    );

    if (isValidOpeningHours) {
      // Get the first day with values to use as the default
      const defaultDay = openingHours.find(day =>
        (day.from && day.to) || day.is24HourAvailable
      );

      // If no day has values, return early (this case should not happen if validation passed)
      if (!defaultDay) return;

      // Fill the array with the default values from the first valid day
      const updatedOpeningHours = Array(7).fill({
        from: defaultDay.from || "09:00",
        to: defaultDay.to || "18:00",
        is24HourAvailable: defaultDay.is24HourAvailable || false,
      });

      setFieldValue("openingHours", updatedOpeningHours);
    } else {
      // Handle validation error or other logic
      // Maybe display a message that at least one day should be filled
    }
  };


  return (
    <div className="my-profile-box-contain">
      <div className="my-profile-box-form">
        <div className="h-100 m-0">
          <div className="form-box-title">
            <h4>Contact Info</h4>
            <p>How and when can clients contact you?</p>
          </div>
          <Formik
            initialValues={existData || initialValues}
            enableReinitialize={true}
            validationSchema={ContactInfoValidation}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form className="cmn-form-set">
                <div className="mb-3">
                  <label htmlFor="email">Contact Email Address</label>
                  <Field
                    name="email"
                    type="text"
                    className="form-control"
                    placeholder="Type here"
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="phoneNumber">Contact Phone Number</label>
                  <PhoneInput
                    country={"gb"}
                    name="phoneNumber"
                    type="phone"
                    containerClass="PhoneInput-custom-container"
                    inputClass="PhoneInput-custom-input"
                    enableSearch={true}
                    value={values.phoneNumber ?  `+${values.countryCode}${values.phoneNumber}` :phone}
                    onChange={(phone ,country) => {
                      setPhone(phone)
                      if(phone){
                        setFieldValue("phoneNumber", phone.slice(country.dialCode.length))
                        setFieldValue('countryCode', country.dialCode);
                      }
                    }}
                  />
                  <ErrorMessage
                    name="phoneNumber"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <label>Business Hours</label>
                        <div>
                          {values?.openingHours.some(day =>
                            (day.from && day.to) || day.is24HourAvailable
                          ) &&
                            <button
                              type="button"
                              className="btn btn-primary fill-all"
                              onClick={() => fillAllOpeningHours(setFieldValue, values?.openingHours)}
                            >
                              Fill all
                            </button>
                          }
                          <span>24hrs</span>
                        </div>
                      </div>

                      <div className="opening-times-list">
                        {values.openingHours.map((day, index) => (
                          <div key={index} className="opening-times-item">
                            <p>
                              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][index]}
                              {day.is24HourAvailable ? null : day.from === "" && day.to === "" && <span> (Closed)</span>}
                            </p>
                            <div>
                              <div>
                                <DatePicker
                                  selected={day.from ? new Date(`1970-01-01T${day.from}:00`) : null}
                                  onChange={(date) =>
                                    setFieldValue(`openingHours[${index}].from`, date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "")
                                  }
                                  showTimeSelect
                                  showTimeSelectOnly
                                  value={day.is24HourAvailable ? "00:00" : day.from}
                                  timeIntervals={15}
                                  timeCaption="From"
                                  dateFormat="HH:mm"
                                  isClearable
                                  disabled={day.is24HourAvailable}
                                  className="form-control custom-datepicker date-picker-left"
                                  placeholderText="Start time"
                                  onKeyDown={(e) => e.preventDefault()} 
                                />

                                <p className="use-to">to</p>
                                <DatePicker
                                  selected={day.to ? new Date(`1970-01-01T${day.to}:00`) : null}
                                  onChange={(date) =>
                                    setFieldValue(`openingHours[${index}].to`, date ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : "")
                                  }
                                  showTimeSelect
                                  showTimeSelectOnly
                                  value={day.is24HourAvailable ? "23:59" : day.to}
                                  timeIntervals={15}
                                  timeCaption="To"
                                  dateFormat="HH:mm"
                                  disabled={day.is24HourAvailable}
                                  isClearable
                                  className="form-control custom-datepicker date-picker-right"
                                   placeholderText="End time"
                                   onKeyDown={(e) => e.preventDefault()} 
                                />

                              </div>
                              <label>
                                <Field
                                  type="checkbox"
                                  name={`openingHours[${index}].is24HourAvailable`}
                                  checked={day.is24HourAvailable}
                                  onChange={() =>
                                    {
                                      setFieldValue(
                                        `openingHours[${index}].is24HourAvailable`,
                                        !day.is24HourAvailable
                                      )
                                      setFieldValue(`openingHours[${index}].from`, "00:00")
                                      setFieldValue(`openingHours[${index}].to`, "23:59")
                                    }
                                  }
                                />
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Col>
                </Row>

                <button type="submit" className="theme-comm-btn">
                  Save
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ContactInfo;
